import React from 'react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import {
  CoverageList, CrystalPanel, Faq, IconCards, Intro, ProductHero, QuoteSection, ScreenTour, SectionTitle, Steps, TrustStrip,
} from '../../components/product/ProductKit';
import { NUMBER_PAGES, NUMBER_SCREENS, NUMBER_STEPS } from '../../data/numbers';
import { NUMBER_PAGE_IDS } from '../../content/siteContentDefaults';
import { useSiteContent, withProductContent } from '../../content/siteContent';

// Number group each page is about: preselected in the Info dialog and in Order (the DID page covers several).
const TYPE_GROUP = { tollfree: 'tollfree', mobile: 'mobile', twoway: '2way', uifn: 'uifn' };
const toQuote = () => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

/** Shared layout for the DID, Toll-Free, Mobile, 2-Way and UIFN pages; content comes from data/numbers.js. */
const NumberTypePage = ({ type }) => {
  const c = useSiteContent(NUMBER_PAGE_IDS[type]);
  const D = withProductContent(c, NUMBER_PAGES[type]);
  const name = D.seo.title;
  const crumb = `${D.short[0].toUpperCase()}${D.short.slice(1)} Numbers`;

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo
        title={name}
        description={D.seo.description}
        path={D.seo.path}
        breadcrumbs={[{ name: 'Phone Numbers', path: '/phone-numbers/all' }, { name: crumb, path: D.seo.path }]}
        jsonLd={[serviceLd({ name: crumb, description: D.seo.description, path: D.seo.path, serviceType: 'Virtual phone numbers' }), faqLd(D.faqs)]}
      />

      <ProductHero
        {...D.hero}
        secondary={{ label: 'Check coverage', href: '#coverage' }}
        media={<CrystalPanel src={D.crystal} />}
      />

      <TrustStrip items={D.trust} />

      <Intro {...D.intro} />

      <section id="coverage" className="scroll-mt-20 bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={`Where you can get ${D.short} numbers`} sub="Live availability from our coverage database. Select a country for number ranges, features and document requirements." />
          <CoverageList filter={D.filter} group={TYPE_GROUP[type]} onEnquire={toQuote} />
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title="Buy and route numbers in minutes" sub="Order numbers, upload documents and set routing yourself in the VOIP2 portal. Click a screen for a closer look." />
          <ScreenTour screens={NUMBER_SCREENS} />
        </div>
      </section>

      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title="Everything included with every number" />
          <IconCards items={D.benefits} />
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title="Who uses them" />
          <IconCards items={D.useCases} columns={4} />
        </div>
      </section>

      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title="Live in four steps" />
          <Steps steps={NUMBER_STEPS} />
        </div>
      </section>

      <Faq faqs={D.faqs} />

      <QuoteSection
        title={`Get ${D.short} numbers`}
        sub="Tell us where you need numbers and how you want calls handled. We will confirm availability, requirements and pricing within one working day."
        service={D.service}
        contextLabel={D.countryLabel || 'Which country do you need?'}
        contextOptions={D.countryOptions}
        related={D.related}
      />
    </div>
  );
};

export default NumberTypePage;
